import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './DobCalendar.css';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const POPUP_WIDTH = 200;
const POPUP_HEIGHT_ESTIMATE = 220;

export default function DobCalendar({ anchorRef, value, onChange, onClose }) {
  const initial = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    const d = new Date();
    d.setFullYear(d.getFullYear() - 20);
    return d;
  }, [value]);

  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const [pos, setPos] = useState(null); // null → not yet positioned, hide first paint
  const [yearListOpen, setYearListOpen] = useState(false);
  const [monthListOpen, setMonthListOpen] = useState(false);
  const rootRef = useRef(null);
  const yearListRef = useRef(null);
  const monthListRef = useRef(null);
  const selectedYearItemRef = useRef(null);

  const selected = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    return m ? { y: parseInt(m[1], 10), mo: parseInt(m[2], 10) - 1, d: parseInt(m[3], 10) } : null;
  }, [value]);

  useEffect(() => {
    const compute = () => {
      const el = anchorRef?.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Align right edge of popup to right edge of anchor, then clamp to viewport.
      let left = r.right - POPUP_WIDTH;
      if (left < 8) left = 8;
      if (left + POPUP_WIDTH > vw - 8) left = vw - POPUP_WIDTH - 8;
      // Flip above the anchor if there's not enough room below.
      let top = r.bottom + 4;
      if (top + POPUP_HEIGHT_ESTIMATE > vh - 8) top = Math.max(8, r.top - POPUP_HEIGHT_ESTIMATE - 4);
      setPos({ top, left });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const handleDocClick = (e) => {
      const anchorEl = anchorRef?.current;
      if (anchorEl && anchorEl.contains(e.target)) return; // ignore clicks on anchor (button toggles)
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose?.();
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (yearListOpen) setYearListOpen(false);
        else if (monthListOpen) setMonthListOpen(false);
        else onClose?.();
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose, anchorRef, yearListOpen, monthListOpen]);

  // Close year/month list when clicking outside its own picker
  useEffect(() => {
    if (!yearListOpen && !monthListOpen) return;
    const handleClick = (e) => {
      if (yearListOpen && yearListRef.current && !yearListRef.current.contains(e.target)) {
        setYearListOpen(false);
      }
      if (monthListOpen && monthListRef.current && !monthListRef.current.contains(e.target)) {
        setMonthListOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [yearListOpen, monthListOpen]);

  // Scroll selected year into view when list opens
  useEffect(() => {
    if (yearListOpen && selectedYearItemRef.current) {
      selectedYearItemRef.current.scrollIntoView({ block: 'center' });
    }
  }, [yearListOpen]);

  const yearList = useMemo(() => {
    const cy = new Date().getFullYear();
    return Array.from({ length: 96 }, (_, i) => cy - 5 - i);
  }, []);

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDow = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Monday = 0

  const goPrev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const goNext = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const pick = (d) => {
    const iso = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    onChange?.(iso);
    onClose?.();
  };

  if (!pos) return null;

  return createPortal(
    <div
      ref={rootRef}
      className="dob-cal-popup"
      style={{ position: 'fixed', top: pos.top, left: pos.left }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="dob-cal-header">
        <button type="button" className="dob-cal-nav" onClick={goPrev} aria-label="Previous month">‹</button>
        <div className="dob-cal-selects">
          <div className="dob-cal-year-picker" ref={monthListRef}>
            <button
              type="button"
              className="dob-cal-select dob-cal-year-trigger"
              onClick={() => { setMonthListOpen(v => !v); setYearListOpen(false); }}
              aria-label="Month"
              aria-expanded={monthListOpen}
            >
              {MONTH_NAMES[viewMonth]}
            </button>
            {monthListOpen && (
              <div className="dob-cal-year-list" role="listbox">
                {MONTH_NAMES.map((name, i) => (
                  <button
                    key={name}
                    type="button"
                    role="option"
                    aria-selected={i === viewMonth}
                    className={`dob-cal-year-item${i === viewMonth ? ' is-selected' : ''}`}
                    onClick={() => { setViewMonth(i); setMonthListOpen(false); }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="dob-cal-year-picker" ref={yearListRef}>
            <button
              type="button"
              className="dob-cal-select dob-cal-year-trigger"
              onClick={() => { setYearListOpen(v => !v); setMonthListOpen(false); }}
              aria-label="Year"
              aria-expanded={yearListOpen}
            >
              {viewYear}
            </button>
            {yearListOpen && (
              <div className="dob-cal-year-list" role="listbox">
                {yearList.map(y => (
                  <button
                    key={y}
                    type="button"
                    ref={y === viewYear ? selectedYearItemRef : null}
                    role="option"
                    aria-selected={y === viewYear}
                    className={`dob-cal-year-item${y === viewYear ? ' is-selected' : ''}`}
                    onClick={() => { setViewYear(y); setYearListOpen(false); }}
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button type="button" className="dob-cal-nav" onClick={goNext} aria-label="Next month">›</button>
      </div>

      <div className="dob-cal-grid">
        {DAY_LABELS.map(d => <div key={d} className="dob-cal-daylabel">{d}</div>)}
        {Array.from({ length: firstDow }).map((_, i) => <div key={`b${i}`} className="dob-cal-blank" />)}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
          const isSelected = selected && selected.y === viewYear && selected.mo === viewMonth && selected.d === d;
          return (
            <button
              key={d}
              type="button"
              className={`dob-cal-day${isSelected ? ' is-selected' : ''}`}
              onClick={() => pick(d)}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>,
    document.body
  );
}
